import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Banner, Modal } from '../design/ui';
import { ALL_STATUS_MAPPING, getStatusLabel } from '../constants/ticketStatuses';
import './TicketExportModal.css';

const INCLUDE_OPTIONS = ['messages', 'ai_drafts', 'carrier_messages', 'ai_logs'];
const POLL_INTERVAL_MS = 3000;

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const filenameFromDisposition = (disposition) => {
  const match = /filename="?([^";]+)"?/.exec(disposition || '');
  return match ? match[1] : 'tickets_export.json';
};

const TicketExportModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [carrierInput, setCarrierInput] = useState('');
  const [includes, setIncludes] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | exporting | polling | done | error
  const [job, setJob] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const cancelledRef = useRef(false);

  useEffect(() => () => { cancelledRef.current = true; }, []);

  const toggleValue = (value, list, setList) => {
    setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    selectedStatuses.forEach((status) => params.append('status', status));
    if (createdFrom) params.append('created_from', createdFrom);
    if (createdTo) params.append('created_to', createdTo);
    carrierInput
      .split(',')
      .map((carrier) => carrier.trim())
      .filter(Boolean)
      .forEach((carrier) => params.append('carrier_name', carrier));
    includes.forEach((value) => params.append('include', value));
    return params;
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  });

  const downloadJobFile = async (jobId) => {
    const response = await axios.get(`/api/tickets/export/jobs/${jobId}/download`, {
      headers: authHeaders(),
    });
    const link = document.createElement('a');
    link.href = response.data.url;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const pollJob = async (jobId) => {
    setPhase('polling');
    // Bounded polling loop (same shape as the conversation regeneration poll).
    for (let attempt = 0; attempt < 200; attempt += 1) {
      if (cancelledRef.current) return;
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (cancelledRef.current) return;
      try {
        const response = await axios.get(`/api/tickets/export/jobs/${jobId}`, {
          headers: authHeaders(),
        });
        const currentJob = response.data.job;
        setJob(currentJob);
        if (currentJob.status === 'done') {
          setPhase('done');
          await downloadJobFile(jobId);
          return;
        }
        if (currentJob.status === 'failed') {
          setPhase('error');
          setErrorMessage(t('ticketExport.jobFailed', 'The export failed. Please try again.'));
          return;
        }
      } catch (error) {
        // Transient poll errors: keep trying until the loop bound.
      }
    }
    setPhase('error');
    setErrorMessage(t('ticketExport.stillRunning', 'The export is still running — close this window and use the export button again later to retrieve it.'));
  };

  const handleExport = async () => {
    setPhase('exporting');
    setErrorMessage('');
    try {
      const response = await axios.get(`/api/tickets/export?${buildParams().toString()}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      if (response.status === 202) {
        const payload = JSON.parse(await response.data.text());
        setJob(payload.job);
        await pollJob(payload.job.id);
      } else {
        triggerBlobDownload(
          response.data,
          filenameFromDisposition(response.headers['content-disposition'])
        );
        setPhase('done');
      }
    } catch (error) {
      setPhase('error');
      setErrorMessage(t('ticketExport.error', 'Export failed. Please try again.'));
    }
  };

  const busy = phase === 'exporting' || phase === 'polling';
  const tl = (key, fallback, options) => t(`ticketListsUi.${key}`, fallback, options);

  return (
    <Modal
      wide
      icon={Download}
      title={t('ticketExport.title', 'Exporter les tickets')}
      description={tl('export.desc', 'Un fichier JSON avec les tickets du compte ; les filtres ci-dessous le réduisent.')}
      onClose={onClose}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={onClose} disabled={busy}>
            {t('ticketExport.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={handleExport} disabled={busy}>
            <Download aria-hidden="true" />
            {busy
              ? t('ticketExport.preparing', 'Préparation…')
              : t('ticketExport.start', 'Exporter en JSON')}
          </button>
        </>
      )}
    >
      <div className="u-form tex-v2">
        <div className="u-field">
          <div className="tex-v2__label-row">
            <span className="u-label">{t('ticketExport.statusLabel', 'Statuts')}</span>
            {selectedStatuses.length > 0 && (
              <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setSelectedStatuses([])}>
                {t('ticketExport.clear', 'Effacer')}
              </button>
            )}
          </div>
          <p className="u-help">
            {selectedStatuses.length === 0
              ? t('ticketExport.allStatuses', 'Aucune sélection = tous les statuts')
              : t('ticketExport.selectedCount', '{{count}} sélectionné(s)', { count: selectedStatuses.length })}
          </p>
          <div className="tex-v2__checks">
            {ALL_STATUS_MAPPING.map(({ value }) => (
              <label key={value} className={`tex-v2__check${selectedStatuses.includes(value) ? ' is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(value)}
                  onChange={() => toggleValue(value, selectedStatuses, setSelectedStatuses)}
                />
                <span>{getStatusLabel(t, value)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="u-form-grid">
          <div className="u-field">
            <label className="u-label" htmlFor="tex-from">{t('ticketExport.dateRange', 'Créés entre')}</label>
            <div className="tex-v2__dates">
              <input
                id="tex-from"
                type="date"
                className="u-input"
                value={createdFrom}
                onChange={(event) => setCreatedFrom(event.target.value)}
                aria-label={t('ticketExport.dateFrom', 'Du')}
              />
              <span className="tex-v2__sep" aria-hidden="true">→</span>
              <input
                type="date"
                className="u-input"
                value={createdTo}
                onChange={(event) => setCreatedTo(event.target.value)}
                aria-label={t('ticketExport.dateTo', 'Au')}
              />
            </div>
          </div>
          <div className="u-field">
            <label className="u-label" htmlFor="tex-carriers">{t('ticketExport.carrierLabel', 'Transporteurs')}</label>
            <input
              id="tex-carriers"
              type="text"
              className="u-input"
              value={carrierInput}
              onChange={(event) => setCarrierInput(event.target.value)}
              placeholder={t('ticketExport.carrierPlaceholder', 'ex. Colissimo, Chronopost (séparés par des virgules)')}
            />
          </div>
        </div>

        <div className="u-field">
          <span className="u-label">{t('ticketExport.includeLabel', "Inclure dans l'export")}</span>
          <div className="tex-v2__checks tex-v2__checks--two">
            {INCLUDE_OPTIONS.map((value) => (
              <label key={value} className={`tex-v2__check${includes.includes(value) ? ' is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={includes.includes(value)}
                  onChange={() => toggleValue(value, includes, setIncludes)}
                />
                <span>{t(`ticketExport.include_${value}`, value)}</span>
              </label>
            ))}
          </div>
        </div>

        {phase === 'polling' && (
          <Banner tone="info">
            {t('ticketExport.asyncNotice', 'Export volumineux : le fichier est généré en arrière-plan.')}{' '}
            {job?.status === 'queued'
              ? t('ticketExport.jobQueued', "En file d'attente…")
              : t('ticketExport.jobRunning', 'Génération du fichier…')}
          </Banner>
        )}
        {phase === 'done' && (
          <Banner tone="success">{t('ticketExport.jobDone', 'Votre fichier est en cours de téléchargement.')}</Banner>
        )}
        {phase === 'error' && (
          <Banner tone="danger">{errorMessage}</Banner>
        )}
      </div>
    </Modal>
  );
};

export default TicketExportModal;
